import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, formatMoney, formatPct } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft,
  Vault,
  ShieldWarning,
  ChartLineUp,
  Coins,
} from "@phosphor-icons/react";

export default function AssetDetail() {
  const { symbol } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [asset, setAsset] = useState(null);
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/assets/${symbol}`).then((r) => setAsset(r.data));
  }, [symbol]);

  const invest = async () => {
    if (!user) {
      nav("/login");
      return;
    }
    const amt = Number(amount);
    if (!amt || amt <= 0) {
      toast.error("Enter an amount to invest");
      return;
    }
    if (amt < asset.min_investment) {
      toast.error(`Minimum investment is $${asset.min_investment}`);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/transactions/invest", {
        asset_symbol: asset.symbol,
        amount: amt,
      });
      toast.success(
        `Invested ${formatMoney(amt)} · ${data.units.toFixed(6)} units`,
      );
      setAmount("");
      setTimeout(() => nav("/dashboard"), 900);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!asset) {
    return (
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-32 text-neutral-500">
        Loading asset…
      </div>
    );
  }

  return (
    <div data-testid={`asset-detail-${asset.symbol}`} className="text-white">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-10">
        <Link
          to={`/marketplace/${asset.category}`}
          className="inline-flex items-center gap-2 text-neutral-400 hover:text-white text-sm"
        >
          <ArrowLeft size={16} /> Back to {asset.category}
        </Link>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-12 grid grid-cols-1 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3">
          <div className="relative aspect-[16/9] overflow-hidden bg-[#141414] border border-white/10 mb-8">
            <img
              src={asset.image}
              alt={asset.name}
              className="w-full h-full object-cover opacity-80"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/80 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6">
              <div className="overline">{asset.symbol}</div>
              <div className="font-display text-3xl">{asset.name}</div>
            </div>
          </div>

          <div className="overline mb-3">Overview</div>
          <h2 className="font-display text-3xl mb-4">
            A restrained thesis, priced with discipline.
          </h2>
          <p className="text-neutral-300 leading-relaxed mb-8">{asset.description}</p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[
              ["Category", asset.category.toUpperCase()],
              ["Risk", asset.risk],
              ["Min Investment", `$${asset.min_investment}`],
              ["Target APY", `${asset.expected_apy}%`],
            ].map(([l, v]) => (
              <div key={l} className="border border-white/10 bg-[#141414] p-4">
                <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">{l}</div>
                <div className="font-display text-lg">{v}</div>
              </div>
            ))}
          </div>

          <div className="border border-[#E6C998]/20 bg-[#141414] p-6 flex gap-4">
            <ShieldWarning size={26} weight="duotone" className="text-[#E6C998] shrink-0 mt-1" />
            <div className="text-sm text-neutral-300 leading-relaxed">
              <div className="overline mb-2">Disclosure</div>
              Returns are not guaranteed. Past performance is not indicative of
              future results. Read the full{" "}
              <Link to="/risk" className="text-[#E6C998] underline">
                risk disclosure
              </Link>{" "}
              before allocating capital.
            </div>
          </div>
        </div>

        {/* Invest panel */}
        <aside className="lg:col-span-2">
          <div className="sticky top-24 lux-card p-6">
            <div className="flex items-center justify-between mb-5">
              <div className="overline">Live · Mark</div>
              <div className="text-xs text-neutral-500">
                <Coins size={14} weight="duotone" className="inline mr-1 text-[#E6C998]" />
                {asset.category === "crypto" ? "CoinGecko" : "Reserved"}
              </div>
            </div>
            <div className="flex items-baseline gap-3 mb-6">
              <div className="font-display text-4xl num">
                {formatMoney(asset.price, { max: asset.price > 1000 ? 0 : 2 })}
              </div>
              <div
                className={`num text-sm ${
                  asset.change_24h >= 0 ? "text-emerald-400" : "text-red-400"
                }`}
              >
                {formatPct(asset.change_24h)} · 24h
              </div>
            </div>

            <label className="block text-xs uppercase tracking-wider text-neutral-400 mb-2">
              Investment amount (USD)
            </label>
            <input
              data-testid="invest-amount-input"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
              placeholder={`Min $${asset.min_investment}`}
              className="w-full bg-black border border-white/20 text-white px-4 py-3 focus:border-[#E6C998] focus:outline-none focus:ring-1 focus:ring-[#E6C998]/40 num"
            />

            <div className="mt-4 text-xs text-neutral-500 flex items-center justify-between">
              <span>Estimated units</span>
              <span className="num text-white">
                {amount ? (Number(amount) / asset.price).toFixed(6) : "—"}
              </span>
            </div>

            <button
              data-testid="invest-submit"
              onClick={invest}
              disabled={submitting}
              className="btn-gold w-full mt-6 py-4 rounded-none text-sm disabled:opacity-60"
            >
              {submitting
                ? "Placing order…"
                : user
                  ? `Invest ${amount ? formatMoney(amount) : ""}`
                  : "Login to invest"}
            </button>

            <div className="mt-5 text-xs text-neutral-500 flex items-start gap-2">
              <Vault size={14} weight="duotone" className="text-[#E6C998] shrink-0 mt-0.5" />
              <span>
                Assets are held under segregated custody. Simulated deposits;
                real custody attested quarterly.
              </span>
            </div>

            <div className="divider-gold my-6" />

            <div className="text-xs uppercase tracking-wider text-neutral-400 mb-3">
              What you get
            </div>
            <ul className="text-sm text-neutral-300 space-y-2">
              <li className="flex gap-3">
                <ChartLineUp size={16} weight="duotone" className="text-[#E6C998] mt-1" />
                Live valuation on your dashboard
              </li>
              <li className="flex gap-3">
                <Vault size={16} weight="duotone" className="text-[#E6C998] mt-1" />
                Segregated, auditable position
              </li>
              <li className="flex gap-3">
                <ShieldWarning size={16} weight="duotone" className="text-[#E6C998] mt-1" />
                Transparent risk framing
              </li>
            </ul>
          </div>
        </aside>
      </section>
    </div>
  );
}
